import React from 'react'
import { GridWrapper, GridItem } from '@sainsburys-tech/grid'
import { agnostifyUrl } from '../../../helpers/utils'
import { ComponentHelper } from '../../../helpers/ComponentHelper'
import { createGolNonContentDataAttributes } from '../../../helpers/tracking'
import { M035Props } from './types'
import { StyledButton } from './M035.styles'

/**
 * - M035 is a category signposts component.
 * - It has repeatable link buttons that sit in a group.
 * - They are intended to link to canned searches (e.g. 'Under £20')
 */
const M035 = ({
  backgroundColor,
  buttonTextColor,
  kind,
  links,
  linkTarget,
  alignment = 'center',
  mobileColumns = false,
  onLinkClick = null
}: M035Props): React.ReactElement => {
  const signpostLength = links.length
  if (signpostLength < 1 || signpostLength > 12) return null

  const addGolTracking = (evt: React.MouseEvent<HTMLButtonElement>, linkName: string) => {
    // click tracking exists for both M035 & M0015
    const dataAttributes = createGolNonContentDataAttributes(
      'link',
      'click',
      'nav button',
      `M035-${linkName.toLowerCase()}`,
      'navigation'
    )

    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event: React.MouseEvent<HTMLButtonElement>, linkUrl: string, linkName: string) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(linkUrl))
    }
    addGolTracking(event, linkName)
  }

  return (
    <div style={{ marginBottom: '16px' }} data-test='m035-container-wrapper'>
      <GridWrapper
        testid='m035-container'
        gap='lg'
        cols={signpostLength > 1 ? 12 : undefined}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        center={alignment === 'center'}
      >
        {links.map((signpost, index) => {
          const signPostSize = ComponentHelper.gridLayout('M035', index, signpostLength)
          const signpostSm = mobileColumns || signpostLength === 1 ? 6 : 12
          return (
            <GridItem
              colSpan={{ sm: signpostSm, ...signPostSize }}
              data-test={`m035-button-${index}-container`}
              key={`m035-signpost-${index}`}
            >
              <StyledButton
                forwardedAs='a'
                data-test='m035-button'
                backgroundColor={backgroundColor}
                buttonTextColor={buttonTextColor}
                href={agnostifyUrl(signpost.linkUrl)}
                target={linkTarget}
                title={signpost.linkTitle}
                onClick={(evt: React.MouseEvent<HTMLButtonElement>) =>
                  ctaClick(evt, signpost.linkUrl, signpost.linkName)
                }
                kind={kind}
                full
                mobileColumns={mobileColumns}
                signpostLength={signpostLength}
              >
                {signpost.linkName}
              </StyledButton>
            </GridItem>
          )
        })}
      </GridWrapper>
    </div>
  )
}

export default M035
